// LoginForm — magic-link OR email+password sign-in.
// Plan ref: 05-RESEARCH-ui-shell.md §Code Examples §Login form.

import { useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useDocumentTitle } from '@/lib/use-document-title';

type Mode = 'magic-link' | 'password';

export function LoginForm(): JSX.Element {
  // Per-route document.title — Plan 06-02 §Task 1 step 7 (the login screen
  // is the 8th canonical 'Studio · <X>' title).
  useDocumentTitle('Studio · Sign In');
  const [mode, setMode] = useState<Mode>('magic-link');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'magic-link') {
        const { error: err } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}/today` },
        });
        if (err) setError(err.message);
        else setSent(true);
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (err) {
          setError(err.message);
        } else {
          // On success, the SessionContext picks up the new session and the
          // RequireAdmin route boundary navigates the user to /today.
          window.location.assign('/today');
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-surface-0 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-xs space-y-4 rounded-lg bg-surface-1 p-6 shadow-soft"
      >
        {/* Brand lockup (brand.md §6: login carries the full identity —
            registration-crosshair mark + tracked wordmark + press line). */}
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <svg
              viewBox="0 0 16 16"
              className="h-5 w-5 text-accent-9"
              aria-hidden="true"
              fill="none"
            >
              <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="2" />
              <rect x="0" y="7.375" width="16" height="1.25" fill="currentColor" />
              <rect x="7.375" y="0" width="1.25" height="16" fill="currentColor" />
            </svg>
            <h1 className="text-lg font-semibold tracking-wide text-fg-default">Studio</h1>
          </div>
          <p className="font-mono text-2xs uppercase tracking-[0.18em] text-fg-muted">
            WishDaily · Content Press
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Sign-in method"
          className="flex rounded border border-border-subtle bg-surface-0 p-0.5 text-xs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'magic-link'}
            onClick={() => {
              setMode('magic-link');
              setError(null);
            }}
            className={`flex-1 rounded px-2 py-1 min-h-[44px] md:min-h-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${mode === 'magic-link' ? 'bg-surface-1 text-fg-default' : 'text-fg-muted hover:text-fg-default'}`}
          >
            Magic link
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'password'}
            onClick={() => {
              setMode('password');
              setSent(false);
              setError(null);
            }}
            className={`flex-1 rounded px-2 py-1 min-h-[44px] md:min-h-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${mode === 'password' ? 'bg-surface-1 text-fg-default' : 'text-fg-muted hover:text-fg-default'}`}
          >
            Password
          </button>
        </div>

        {sent && mode === 'magic-link' ? (
          <p className="text-sm text-fg-muted">Check your email for the magic link.</p>
        ) : (
          <>
            <label className="block text-sm">
              <span className="sr-only">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full min-h-[44px] rounded border border-border-subtle bg-surface-0 px-3 py-2 text-fg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus md:min-h-0"
                placeholder="admin@wishdaily.app"
                autoComplete="email"
              />
            </label>

            {mode === 'password' ? (
              <label className="block text-sm">
                <span className="sr-only">Password</span>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full min-h-[44px] rounded border border-border-subtle bg-surface-0 px-3 py-2 text-fg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus md:min-h-0"
                  placeholder="Password"
                  autoComplete="current-password"
                  minLength={6}
                />
              </label>
            ) : null}

            {error ? <p className="text-sm text-danger-11">{error}</p> : null}

            <button
              type="submit"
              disabled={busy}
              className="min-h-[44px] w-full rounded bg-accent-9 py-2 text-fg-on-accent disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1 md:min-h-0"
            >
              {busy ? 'Working…' : mode === 'magic-link' ? 'Send link' : 'Sign in'}
            </button>
          </>
        )}
      </form>
    </main>
  );
}
